import useScrollLock from '@/hooks/useScrollLock';
import { useState } from 'react';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Check, Copy, Share2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function ShareButton({ 
  shareUrl, 
  title = "Share",
  description = "Share this link with others"
}) {
  const [copied, setCopied] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  useScrollLock(showUrl);

  const fullUrl = `${window.location.origin}${shareUrl}`;

  const handleShare = async () => {
    // Try native share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: fullUrl
        });
      } catch (err) {
        // User cancelled - this is fine
        if (err.name !== 'AbortError') {
          logger.error('Share failed:', err);
        }
      }
    }
  };

  const handleCopy = async () => {
    try {
      // Try modern clipboard API first (works in production with HTTPS)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for preview/HTTP environment - try execCommand
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopied(true);
          toast.success('Link copied to clipboard!');
          setTimeout(() => setCopied(false), 2000);
        } else {
          // Show URL for manual copy if execCommand fails
          setShowUrl(true);
        }
      }
    } catch (err) {
      // Show URL for manual copy
      setShowUrl(true);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Share Button */}
        <Button
          onClick={handleShare}
          variant="outline"
          className="flex items-center gap-2"
          style={{ borderColor: '#9E6752', color: '#9E6752' }}
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </Button>

        {/* Copy Link Button */}
        <Button
          onClick={handleCopy}
          variant="outline"
          className="flex items-center gap-2"
          style={{ 
            borderColor: copied ? '#10B981' : '#2D4354',
            color: copied ? '#10B981' : '#2D4354',
            background: copied ? '#D1FAE5' : 'white'
          }}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              <span className="hidden sm:inline">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span className="hidden sm:inline">Copy Link</span>
            </>
          )}
        </Button>
      </div>

      {/* Simple URL display for manual copy (preview only) */}
      {showUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowUrl(false)}>
          <div className="bg-white rounded-lg p-4 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium">Link</span>
              <button onClick={() => setShowUrl(false)}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              type="text"
              value={fullUrl}
              readOnly
              className="w-full p-2 text-sm border rounded"
              onClick={(e) => e.target.select()}
            />
            <p className="text-xs text-gray-500 mt-2 text-center">Tap to select, then copy</p>
          </div>
        </div>
      )}
    </>
  );
}
